/**
 * Config Transformer - Converts legacy section data to NeuroplasticSectionConfig
 * 
 * This module transforms existing section data formats into the unified
 * NeuroplasticSectionConfig format for dynamic rendering.
 */

import { 
  NeuroplasticSectionConfig, 
  LayoutType, 
  BackgroundConfig,
  ContentBlockConfig,
  MediaBlockConfig,
  CTAConfig,
  CardGridConfig,
  FeatureListConfig,
} from './types';

// ============================================================================
// Section Type to Layout Mapping
// ============================================================================

const sectionTypeToLayout: Record<string, LayoutType> = {
  // Hero types
  'HERO': 'hero',
  'SERVICES_HERO': 'hero',
  'CONTACT_HERO': 'hero',
  'VIDEO_INTRO': 'split',
  
  // About types
  'ABOUT': 'hero',
  'STORY': 'stacked',
  'PEOPLE': 'split',
  'COMMITMENT': 'split-reverse',
  'CTA': 'centered',
  
  // Content types
  'CONTACT': 'split',
  'CONTACT_FAQ': 'stacked',
  'FORM': 'split',
  'HUMAN_RESOURCES': 'split',
  
  // Grid types
  'DETAILED_SOLUTIONS': 'stacked',
  'COMMUNITY': 'grid',
  'BENEFIT_LOGOS_CAROUSEL': 'full-width',
  
  // Benefits types
  'BENEFITS_CULTURE': 'split',
  'BENEFITS_CAREER': 'grid',
  'BENEFITS_PARTNERS_VIDEO': 'split',
  
  // Other types
  'BEYOND_STANDARDS': 'hero',
  'APPLY_NOW': 'centered',
};

// ============================================================================
// Transform Functions
// ============================================================================

function transformBackground(sectionData: Record<string, unknown>, background?: Record<string, unknown>): BackgroundConfig | undefined {
  // Check for video/image in sectionData
  const backgroundVideo = sectionData.backgroundVideo as string | undefined;
  const backgroundImage = sectionData.backgroundImage as string | { desktop?: string; mobile?: string } | undefined;
  
  if (backgroundVideo) {
    return {
      type: 'video',
      value: backgroundVideo,
      overlay: 'black',
      overlayOpacity: 0.3,
    };
  }
  
  if (backgroundImage) {
    const imageUrl = typeof backgroundImage === 'string' 
      ? backgroundImage 
      : backgroundImage.desktop || backgroundImage.mobile;
    const mobileUrl = typeof backgroundImage === 'object' ? backgroundImage.mobile : undefined;
    
    // Check if it's a video URL
    const isVideo = imageUrl && (
      imageUrl.includes('.mp4') || 
      imageUrl.includes('.webm') || 
      imageUrl.includes('.mov')
    );
    
    return {
      type: isVideo ? 'video' : 'image',
      value: imageUrl,
      mobileValue: mobileUrl,
      overlay: 'black',
      overlayOpacity: 0.3,
    };
  }
  
  // Check background object
  if (background) {
    const bgType = background.type as string;
    const bgValue = background.value as string;
    
    if (bgType === 'COLOR') {
      return {
        type: 'color',
        value: bgValue,
      };
    }
    
    if (bgType === 'IMAGE') {
      return {
        type: 'image',
        value: bgValue,
      };
    }
  }
  
  return undefined;
}

function transformContent(sectionData: Record<string, unknown>, styles?: Record<string, unknown>): ContentBlockConfig {
  return {
    badge: sectionData.subtitle as string || sectionData.badge as string,
    title: sectionData.title as string,
    subtitle: sectionData.highlight as string,
    description: sectionData.description as string || sectionData.content as string,
    textColor: styles?.textColor as string,
  };
}

function transformMedia(sectionData: Record<string, unknown>): MediaBlockConfig | undefined {
  const image = sectionData.image as string;
  const video = sectionData.videoUrl as string || sectionData.video as string;
  // videoThumbnail could be used for poster image in future
  
  if (video) {
    return {
      type: 'video',
      src: video,
      alt: sectionData.imageAlt as string || 'Video',
      aspectRatio: 'video',
      autoplay: false,
      loop: false,
      muted: true,
      controls: false,
      showPlayButton: true,
      rounded: true,
      shadow: true,
    };
  }
  
  if (image) {
    return {
      type: 'image',
      src: image,
      alt: sectionData.imageAlt as string || 'Image',
      aspectRatio: 'video',
      rounded: true,
      shadow: true,
    };
  }
  
  return undefined;
}

function transformCTA(sectionData: Record<string, unknown>): CTAConfig | undefined {
  const ctaText = sectionData.ctaText as string;
  const ctaLink = sectionData.ctaLink as string;
  const secondaryCtaText = sectionData.secondaryCtaText as string;
  const secondaryCtaLink = sectionData.secondaryCtaLink as string;
  
  if (!ctaText && !secondaryCtaText) return undefined;
  
  return {
    primary: ctaText && ctaLink ? {
      text: ctaText,
      href: ctaLink,
      icon: 'arrow',
      iconPosition: 'right',
    } : undefined,
    secondary: secondaryCtaText && secondaryCtaLink ? {
      text: secondaryCtaText,
      href: secondaryCtaLink,
      variant: 'outline',
    } : undefined,
  };
}

function transformCards(sectionData: Record<string, unknown>): CardGridConfig | undefined {
  // Check for various card array formats
  const solutions = sectionData.solutions as Array<Record<string, unknown>>;
  const cards = sectionData.cards as Array<Record<string, unknown>>;
  const features = sectionData.features as Array<Record<string, unknown>>;
  const benefits = sectionData.benefits as Array<Record<string, unknown>>;
  
  const items = solutions || cards || features || benefits;
  
  if (!items || items.length === 0) return undefined;
  
  return {
    cards: items.map((item, index) => ({
      id: item.id as string || `card-${index}`,
      title: item.title as string,
      description: item.description as string || item.content as string,
      image: item.image as string,
      icon: item.icon as string,
      badge: item.badge as string,
      link: item.link as string || item.href as string,
    })),
    columns: items.length <= 2 ? 2 : items.length <= 3 ? 3 : 4,
    cardStyle: 'default',
  };
}

function transformFeatures(sectionData: Record<string, unknown>): FeatureListConfig | undefined {
  const features = sectionData.features as Array<Record<string, unknown>>;
  const items = sectionData.items as string[];
  const points = sectionData.points as string[];
  
  if (features && features.length > 0) {
    return {
      features: features.map(f => ({
        title: f.title as string,
        description: f.description as string,
        icon: f.icon as string,
      })),
      layout: 'grid',
      columns: 2,
    };
  }
  
  if (items && items.length > 0) {
    return {
      features: items.map(item => ({ title: item })),
      layout: 'list',
    };
  }
  
  if (points && points.length > 0) {
    return {
      features: points.map(point => ({ title: point })),
      layout: 'list',
    };
  }
  
  return undefined;
}

// ============================================================================
// Main Transform Function
// ============================================================================

export function transformToNeuroplasticConfig(
  section: {
    id: string;
    sectionKey: string;
    title?: string | null;
    type?: string | null;
    sectionData?: Record<string, unknown> | null;
    styles?: Record<string, unknown> | null;
    background?: Record<string, unknown> | null;
  }
): NeuroplasticSectionConfig {
  const sectionData = section.sectionData || {};
  const styles = section.styles || {};
  const background = section.background || {};
  
  // Determine layout from section type
  const layout = sectionTypeToLayout[section.type || ''] || 'stacked';
  
  return {
    id: section.id,
    sectionKey: section.sectionKey,
    type: section.type || undefined,
    layout,
    background: transformBackground(sectionData, background),
    content: transformContent(sectionData, styles),
    media: transformMedia(sectionData),
    cta: transformCTA(sectionData),
    cards: transformCards(sectionData),
    features: transformFeatures(sectionData),
    sectionData, // Keep raw data for backward compatibility
    styles,
  };
}

export default transformToNeuroplasticConfig;
